import React, { useState, useCallback, useMemo } from "react";
import { Input, Button, Form, Dropdown, Select } from "antd";
import { ApolloError } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import type { MenuProps } from "antd";

import LoadingOverlay from "components/loading-overlay/loading-overlay";

import { createLabelForFilters, voidFnc } from "utils/helpers";
import regex from "constants/regex";
import routeNames from "constants/route-names";
import { UserRoleValue, UserRoleInvitationLabel, OrganizationType, isAuthorityRole } from "constants/field-values";
import { useLoggedUser } from "state/reactive-vars/logged-user-var";
import { Properties } from "graphql/generated/graphql";
import { useGetPropertiesByOrganizationIdQuery } from "graphql/queries/get-properties-by-organization-id.generated";
import { useSendNewInvitationMutation } from "graphql/mutations/send-new-invitation.generated";
import { useGetOrganizationsByWorkspaceIdQuery } from "graphql/queries/get-organizations-by-workspace-id.generated";

const UserAdd: React.FC = () => {
	const navigate = useNavigate();
	const { loggedUserValue } = useLoggedUser();
	const [selectedOrganizationId, setSelectedOrganizationId] = useState<string>(loggedUserValue.organization_id || "");
	const [email, setEmail] = useState<string>("");
	const [role, setRole] = useState<UserRoleValue | string>("");
	const [propertiesDropdownOpen, setPropertiesDropdownOpen] = useState<boolean>(false);
	const [selectedPropertiesKeys, setSelectedPropertiesKeys] = useState<Array<string>>([]);
	const [selectedProperties, setSelectedProperties] = useState<Properties[]>([]);
	const [loading, setLoading] = useState<boolean>(false);
	const baseUrl = typeof window !== "undefined" ? window.location.origin : "";
	const signupUrl = `${baseUrl}/signup`;

	const handleOnCompleteGetOrganizationsByWorkspaceId = useCallback(data => {
		// console.log("Complete Get Organizations By WorkspaceId data: ", data);
	}, []);

	const handleOnErrorGetOrganizationsByWorkspaceId = useCallback((error: ApolloError) => {
		console.log("Error Get Organizations By WorkspaceId Error: ", error);
	}, []);

	const { data: organizationsByWorkspaceData, loading: organizationsByWorkspaceLoading } =
		useGetOrganizationsByWorkspaceIdQuery({
			skip: !loggedUserValue.workspace_id,
			variables: {
				workspace_id: loggedUserValue.workspace_id,
				org_types: [OrganizationType.Customer, OrganizationType.Vendor, OrganizationType.Authority]
			},
			errorPolicy: "none",
			fetchPolicy: "network-only",
			nextFetchPolicy: "network-only",
			notifyOnNetworkStatusChange: true,
			onError: handleOnErrorGetOrganizationsByWorkspaceId,
			onCompleted: handleOnCompleteGetOrganizationsByWorkspaceId
		});

	const handleOnCompleteGetPropertiesByOrganizationId = useCallback(data => {
		// console.log("handleOnErrorOrganizations Complete data: ", data);
	}, []);

	const handleOnErrorGetPropertiesByOrganizationId = useCallback((error: ApolloError) => {
		console.log("useGetPropertiesByOrganizationIdQuery Error: ", error);
	}, []);

	const { data: propertiesByOrganizationData, loading: propertiesByOrganizationLoading } =
		useGetPropertiesByOrganizationIdQuery({
			skip: !selectedOrganizationId,
			variables: {
				organization_id: selectedOrganizationId
			},
			errorPolicy: "none",
			fetchPolicy: "network-only",
			nextFetchPolicy: "network-only",
			notifyOnNetworkStatusChange: true,
			onError: handleOnErrorGetPropertiesByOrganizationId,
			onCompleted: handleOnCompleteGetPropertiesByOrganizationId
		});

	const handleOnErrorNewInvitation = useCallback((error: ApolloError) => {
		console.log("sendNewInvitationMutation Error: ", error);
		// setError("There was an error sending your request. Please try again.");
	}, []);

	const handleOnCompletedNewInvitation = useCallback((data: any) => {
		navigate(routeNames.ADMIN_USERS);
	}, []);

	const [sendNewInvitationMutation, { data, loading: loadingSendNewInvitationMutation, error }] =
		useSendNewInvitationMutation({
			errorPolicy: "none",
			fetchPolicy: "network-only",
			notifyOnNetworkStatusChange: true,
			onError: handleOnErrorNewInvitation,
			onCompleted: handleOnCompletedNewInvitation
		});

	const handleEmailChange = useCallback((event: React.ChangeEvent<HTMLInputElement>): void => {
		const value = event.target.value;

		if (regex.email.test(value) || value === "") {
			setEmail(value);
		}
	}, []);

	const [propertiesOptions, propertyDropdownLabel = "Properties"] = useMemo(() => {
		if (loggedUserValue?.user_properties?.length) {
			let selectedPropertyName;

			const productPropertiesValues = loggedUserValue?.user_properties.map(up => up.property);

			const areAllOrNonePropertiesSelected =
				selectedPropertiesKeys.length === productPropertiesValues.length || selectedPropertiesKeys.length === 0;

			if (productPropertiesValues?.length) {
				const productProperties: MenuProps["items"] = productPropertiesValues.reduce(
					(options, property, currentindex) => {
						const optionId = `property-dropdown-option-${currentindex + 1}-${property?.id}`;

						const isSelected = selectedPropertiesKeys.includes(property?.id);

						if (!selectedPropertyName && isSelected) {
							selectedPropertyName = property?.name;
						}

						const newOption = {
							key: `${property?.id}`,
							icon: (
								<div>
									<input
										className="custom-checkbox"
										id={optionId}
										type="checkbox"
										onClick={e => e.stopPropagation()}
										checked={isSelected}
										onChange={voidFnc}
									/>
									<label htmlFor={optionId} className="custom-checkbox-label">
										{property?.name}
									</label>
								</div>
							)
						};

						options.push(newOption);

						return options;
					},
					[
						{
							key: "property-dropdown-option-all",
							icon: (
								<div>
									<input
										className="custom-checkbox"
										id="property-dropdown-option-all"
										type="checkbox"
										onClick={e => e.stopPropagation()}
										checked={areAllOrNonePropertiesSelected}
										onChange={voidFnc}
									/>
									<label htmlFor="property-dropdown-option-all" className="custom-checkbox-label">
										All properties
									</label>
								</div>
							)
						}
					]
				);

				return [productProperties, createLabelForFilters("Properties", selectedPropertiesKeys, selectedPropertyName)];
			}
		}

		return [[], "Properties"];
	}, [selectedPropertiesKeys]);

	// Check if all properties are selected
	const allPropertiesSelected = useMemo(() => {
		// Get count of valid properties
		const validPropertiesCount = loggedUserValue.user_properties
			? loggedUserValue.user_properties.filter(up => up.property)?.length
			: 0;

		return validPropertiesCount > 0 && validPropertiesCount === selectedPropertiesKeys.length;
	}, [loggedUserValue.user_properties, selectedPropertiesKeys]);

	// Helper function to handle "All properties" toggle
	const handleAllPropertiesSelection = useCallback(() => {
		// If all properties are currently selected, deselect all
		if (allPropertiesSelected) {
			setSelectedPropertiesKeys([]);
			setSelectedProperties([]);
		} else {
			const properties: Properties[] = [];
			const propKeys: string[] = [];
			loggedUserValue.user_properties.forEach(up => {
				if (up.property) {
					properties.push(up.property);
					propKeys.push(up.property.id);
				}
			}, []);

			setSelectedPropertiesKeys(propKeys);
			setSelectedProperties(properties);
		}
	}, [loggedUserValue.user_properties, allPropertiesSelected]);

	const handleOnRemoveProperty = useCallback(
		propertyId => {
			const newSelectedProperties = selectedProperties?.filter(element => element.id !== propertyId);

			const newSelectedPropertiesKeys = selectedPropertiesKeys?.filter(element => element !== propertyId);

			setSelectedProperties(newSelectedProperties as Array<never>);
			setSelectedPropertiesKeys(newSelectedPropertiesKeys);
		},
		[selectedProperties, selectedPropertiesKeys]
	);

	const handleOnSelectProperties = useCallback(
		args => {
			const { key: propertyId, selectedKeys } = args;

			// Handle "All properties" selection
			if (propertyId === "property-dropdown-option-all") {
				handleAllPropertiesSelection();
				return;
			}

			const isRemove = !selectedKeys.includes(propertyId);

			if (isRemove) {
				handleOnRemoveProperty(propertyId);
				return;
			}

			if (loggedUserValue?.user_properties) {
				const selectedUserProperty = loggedUserValue.user_properties.find(up => up.property?.id === propertyId);

				if (selectedUserProperty?.property) {
					// Check if the property is already in the filter
					if (!selectedPropertiesKeys.includes(selectedUserProperty.property.id)) {
						setSelectedPropertiesKeys([...selectedPropertiesKeys, selectedUserProperty.property.id]);

						setSelectedProperties([...selectedProperties, selectedUserProperty?.property]);
					}
				}
			}
		},
		[
			loggedUserValue.user_properties,
			handleAllPropertiesSelection,
			handleOnRemoveProperty,
			selectedPropertiesKeys,
			selectedProperties
		]
	);

	const availableRolesValues = useMemo(() => {
		let availableRoles: UserRoleValue[] = [];

		if (organizationsByWorkspaceData?.organizations && selectedOrganizationId) {
			const organization = organizationsByWorkspaceData?.organizations.find(
				element => element.id === selectedOrganizationId
			);

			if (organization && organization.type === OrganizationType.Vendor) {
				availableRoles.push(UserRoleValue.vendor_manager, UserRoleValue.vendor_employee);
			} else if (organization && organization.type === OrganizationType.Authority) {
				availableRoles.push(UserRoleValue.authority_manager, UserRoleValue.authority_user);
			} else {
				availableRoles.push(UserRoleValue.customer_manager, UserRoleValue.customer_employee);
			}
		} else {
			switch (loggedUserValue.role) {
				case UserRoleValue.admin_user:
					availableRoles = Object.values(UserRoleValue);
					break;
				case UserRoleValue.customer_manager:
					availableRoles.push(UserRoleValue.customer_manager, UserRoleValue.customer_employee);
					break;
				case UserRoleValue.vendor_manager:
					availableRoles.push(UserRoleValue.vendor_manager, UserRoleValue.vendor_employee);
					break;
				case UserRoleValue.authority_manager:
					availableRoles.push(UserRoleValue.authority_manager, UserRoleValue.authority_user);
					break;
				default:
					break;
			}
		}

		return availableRoles;
	}, [loggedUserValue.role, selectedOrganizationId, organizationsByWorkspaceData]);

	// An authority user does not get properties one by one: they reach every property that has an inspection or a plan review filed under their organization's jurisdiction, and
	// the backend grants that on sign-up. Picking properties here would do nothing.
	const showPropertiesSelector = !isAuthorityRole(role);

	// Anything picked before switching to an AHJ role would travel in the invitation
	// without ever being applied.
	const handleOnSelectRole = useCallback((value: UserRoleValue | string) => {
		setRole(value);

		if (isAuthorityRole(value)) {
			setSelectedProperties([]);
			setSelectedPropertiesKeys([]);
			setPropertiesDropdownOpen(false);
		}
	}, []);

	const handleSendInvitation = useCallback(async () => {
		if (email) {
			try {
				setLoading(true);

				const selectedPropertiesIds = selectedProperties.map(element => {
					const { id } = element;
					return id;
				});

				const response = await sendNewInvitationMutation({
					variables: {
						email: email,
						role: role,
						message: "",
						workspace_id: loggedUserValue.workspace_id,
						organization_id: selectedOrganizationId,
						link: `${signupUrl}?email=${encodeURIComponent(email)}`,
						hostname: window.location.hostname,
						user_properties: selectedPropertiesIds
					}
				});
			} catch (error) {
				console.error("handleSendInvitation Error:", error);
			} finally {
				setLoading(false);
			}
		}
	}, [email, loggedUserValue, selectedProperties, window.location, role, selectedOrganizationId]);

	const isLoading =
		loading || propertiesByOrganizationLoading || loadingSendNewInvitationMutation || organizationsByWorkspaceLoading;

	return (
		<div className="basic-screen-container">
			<span className="add-product-page-title">{"New User Invitation"}</span>

			<Form className="add-product-form" autoComplete="off">
				<span className="add-product-form-title">{"New User Details"}</span>

				{[UserRoleValue.admin_user, UserRoleValue.vendor_manager].includes(
					(loggedUserValue.role || "") as UserRoleValue
				) ? (
					<Form.Item
						className="login-form-item"
						rules={[
							{
								required: true,
								message: "Please select the user Organization."
							}
						]}
						required
						validateTrigger="onBlur"
					>
						<Select
							value={selectedOrganizationId || undefined}
							className="add-product-input-select"
							onChange={value => {
								if (value !== selectedOrganizationId) {
									setSelectedOrganizationId(value);
									setSelectedProperties([]);
									setSelectedPropertiesKeys([]);
								}
							}}
							options={organizationsByWorkspaceData?.organizations?.map(element => {
								return { value: element.id, label: element.name };
							})}
							placeholder="User Organization"
							allowClear
							disabled={isLoading}
							suffixIcon={
								<img
									src={process.env.PUBLIC_URL + "/assets/images/icons/black-chevron-down.svg"}
									alt="dropdown icon"
									className="chevron-button"
								/>
							}
						/>
					</Form.Item>
				) : null}

				<Form.Item
					name="email"
					className="login-form-item"
					rules={[
						{ required: true, message: "Please input your Email." },
						{ type: "email", message: "Please provide a valid email address." }
					]}
					required
					validateTrigger="onBlur"
				>
					<Input
						name="email"
						placeholder="User Email"
						className="add-product-input-pair"
						value={email}
						autoComplete="off"
						onChange={handleEmailChange}
						allowClear
						//disabled={isLoading}
					/>
				</Form.Item>

				{availableRolesValues.length ? (
					<Form.Item
						className="login-form-item"
						rules={[{ required: true, message: "Please select the user role." }]}
						required
						validateTrigger="onBlur"
					>
						<Select
							value={role || undefined}
							className="add-product-input-select"
							onChange={handleOnSelectRole}
							options={availableRolesValues.map(element => {
								return {
									value: element,
									label: UserRoleInvitationLabel[element]
								};
							})}
							placeholder="User Role"
							allowClear
							disabled={isLoading}
							suffixIcon={
								<img
									src={process.env.PUBLIC_URL + "/assets/images/icons/black-chevron-down.svg"}
									alt="dropdown icon"
									className="chevron-button"
								/>
							}
						/>
					</Form.Item>
				) : null}

				{showPropertiesSelector ? (
					<>
						<Dropdown
							menu={{
								items: propertiesOptions,
								selectable: true,
								multiple: true,
								onSelect: args => handleOnSelectProperties(args),
								onDeselect: args => handleOnSelectProperties(args),
								selectedKeys: selectedPropertiesKeys,
								style: { maxHeight: 300, overflowY: "auto" }
							}}
							arrow
							className="organization-properties-dropdown"
							overlayClassName="products-dropdown-overlay"
							trigger={["click"]}
							open={propertiesDropdownOpen}
							onOpenChange={(open: boolean, info: { source: "trigger" | "menu" }) => {
								if (info.source === "trigger" && open === false) {
									setPropertiesDropdownOpen(false);
								}
							}}
							disabled={isLoading || propertiesOptions?.length === 0}
						>
							<Button
								onClick={() => {
									setPropertiesDropdownOpen(previousValue => !previousValue);
								}}
								disabled={isLoading || propertiesOptions?.length === 0}
							>
								{propertyDropdownLabel}
								<img
									src={process.env.PUBLIC_URL + "/assets/images/icons/black-chevron-down.svg"}
									style={{ width: "1em", height: "1em" }}
									className="chevron-button"
									title={propertyDropdownLabel}
								/>
							</Button>
						</Dropdown>

						<div className="organization-selected-properties-container">
							{selectedProperties.map((property: Properties) => {
								return (
									<div key={property.id} className="organization-selected-property">
										<span className="organization-selected-property-name">{property.name}</span>
										<span
											className="organization-selected-property-delete"
											onClick={() => {
												if (!isLoading) {
													handleOnRemoveProperty(property.id);
												}
											}}
										>
											{"X"}
										</span>
									</div>
								);
							})}
						</div>
					</>
				) : null}
			</Form>
			<div className="add-product-button-container">
				<Button
					className="admin-add-product-button"
					htmlType="submit"
					onClick={handleSendInvitation}
					disabled={isLoading}
				>
					Send
				</Button>
			</div>

			<LoadingOverlay isLoading={isLoading} />
		</div>
	);
};

export default UserAdd;
